export type AccessRole =
	| 'System Admin'
	| 'Global Admin'
	| 'Global User'
	| 'Admin'
	| 'Staff'
	| 'User'
	| '';

export interface AccessUser {
	purview?: Record<string, string[]>;
	customer?: { idNumber?: number | null };
}

export interface Access {
	canLogin: boolean;
	isSystemAdmin: boolean;
	isSuperAdmin: boolean;
	isSuperUser: boolean;
	isAdmin: boolean;
	isStaff: boolean;
	isUser: boolean;
	isGlobalRole: boolean;
	role: AccessRole;
}

export interface ResolveAccessOptions {
	appId: string;
	allowUserLogin?: boolean;
}

/**
 * Resolves a user's role flags from purview[appId] (falling back to purview.DEFAULT).
 * Pure and null-safe; global flags require a customer idNumber.
 */
export const resolveAccess = (
	user: AccessUser | null | undefined,
	options: ResolveAccessOptions,
): Access => {
	const purview = user?.purview?.[options.appId] ?? user?.purview?.DEFAULT;
	const hasCustomer = user?.customer?.idNumber != null;
	const has = (...tokens: string[]): boolean =>
		Array.isArray(purview) && tokens.some((token) => purview.indexOf(token) !== -1);

	const isAdmin = has('admin');
	const isStaff = has('staff');
	const isUser = has('user');
	const isSystemAdmin = hasCustomer && has('sys', 'systemAdmin');
	const isSuperAdmin = hasCustomer && has('superadmin', 'globalAdmin');
	const isSuperUser = hasCustomer && has('superuser', 'superUser', 'globalStaff', 'globalstaff');
	const isGlobalRole = isSystemAdmin || isSuperAdmin || isSuperUser;

	const canLogin = options.allowUserLogin
		? isAdmin || isStaff || isUser || isGlobalRole
		: isAdmin || isGlobalRole;

	let role: AccessRole = '';
	if (isSystemAdmin) role = 'System Admin';
	else if (isSuperAdmin) role = 'Global Admin';
	else if (isSuperUser) role = 'Global User';
	else if (isAdmin) role = 'Admin';
	else if (isStaff) role = 'Staff';
	else if (isUser) role = 'User';

	return {
		canLogin,
		isSystemAdmin,
		isSuperAdmin,
		isSuperUser,
		isAdmin,
		isStaff,
		isUser,
		isGlobalRole,
		role,
	};
};
